import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { colors } from '../../../../../../colorVars';
import { useWebsocketStore } from '../../../../../../stores/websocketStore/websocketProvider';
import BidAskBox from '../../../../charts/d3/candlestickchart/newCandleChart/chartHeader/BidAskBox';
import PnLDisplay from '../pnLDisplay/PnLDisplay';
import {
  mapAccountsFromBalances,
  resolveMarketDataBrokerage,
} from '../../../../../../utils/accountRouting';
import {
  definingSetupDetails,
  definingTradeDirection,
  determineStatus,
} from '../tradePlanIdentityDisplay/identityDisplayUtils';
import { MarketDataHealthDot } from '../marketDataHealthDot/MarketDataHealthDot';
import { CloseRow } from './closeRow/CloseRow';
import style from './InPositionTopRow.module.css';
import { OpenRow } from './openRow/OpenRow';
import { TopRowBox } from './topRowBox/TopRowBox';
import { TopRowFillPrice } from './topRowBox/TopRowFillPrice';
import { TopRowExpandButton } from './topRowExpandButton/TopRowExpandButton';

const directionColorMap = {
  SHORT: colors.red,
  LONG: colors.green,
  BOTH: colors.blue,
};
const statusColorMap = {
  ARMED: colors.blue,
  ENTERED: colors.green,
  PAUSED: colors.orange,
};

const positionDirection = (tradePlan, metrics) => {
  const position = metrics?.position;
  if (position?.sideLong === true) return 'LONG';
  if (position?.sideLong === false) return 'SHORT';

  const side = `${position?.side ?? position?.quantityDirection ?? ''}`.toLowerCase();
  if (side.includes('long') || side.includes('buy')) return 'LONG';
  if (side.includes('short') || side.includes('sell')) return 'SHORT';

  return definingTradeDirection(tradePlan);
};

export const formatPositionQuantity = (metrics, tradePlan, direction) => {
  const quantity =
    metrics?.openQuantity ?? metrics?.entryQuantity ?? tradePlan?.totalTradePlanQty;
  const numericQuantity = Number(quantity);
  if (!Number.isFinite(numericQuantity) || numericQuantity <= 0) {
    return null;
  }
  const signedQuantity = direction === 'SHORT' ? -Math.abs(numericQuantity) : Math.abs(numericQuantity);
  return String(signedQuantity);
};

const SELECTED_TOP_ROW_GLOW = `0 0 0 1.5px ${colors.blue}, 0 0 9px 2px ${colors.blue}99`;

export const InPositionTopRow = observer(({ tradePlanId }) => {
  const { tradePlanStore, accountBalanceStore, tradePlanSelectionStore } = useWebsocketStore();
  const tradePlan = tradePlanStore.tradePlans[tradePlanId] ?? null;
  if (!tradePlan) return null;
  const selected = tradePlanSelectionStore.isSelected(tradePlanId);
  const metrics = tradePlanStore.getTradePlanPositionMetrics(tradePlanId);
  const activeSymbol =
    tradePlan.positionSymbol?.startsWith('/') &&
    tradePlan.positionSymbol !== tradePlan.symbol
      ? tradePlan.positionSymbol
      : tradePlan.symbol;
  const accounts = mapAccountsFromBalances(accountBalanceStore?.balances);
  const marketDataBrokerage = resolveMarketDataBrokerage({
    accountNumber: tradePlan.accountNumber,
    accounts,
    currentBrokerage: tradePlan.brokerage,
    dataSource: tradePlan.dataSource,
    symbol: activeSymbol,
  });
  
  const direction = positionDirection(tradePlan, metrics);
  const status = determineStatus(tradePlan);
  const positionQuantity = formatPositionQuantity(metrics, tradePlan, direction);
  const [type, setType] = useState('pct');

  return (
    <div className={style.container}>
      <div
        className={style.topRow}
        style={
          selected
            ? { boxShadow: SELECTED_TOP_ROW_GLOW, borderRadius: '4px' }
            : undefined
        }
      >
        <TopRowBox
          backgroundColor={colors.grey}
          color={colors.lightGrey}
          width="100px"
        >
          <>
            {activeSymbol}
            <MarketDataHealthDot status={tradePlan.marketDataHealthStatusNormalized} />
          </>
        </TopRowBox>
        <TopRowBox
          backgroundColor={colors.grey}
          color={directionColorMap[direction]}
          width="100px"
        >
          {direction}
        </TopRowBox>
        <TopRowBox
          backgroundColor={colors.grey}
          color={positionQuantity ? directionColorMap[direction] : statusColorMap[status]}
          width="100px"
        >
          {positionQuantity ?? status}
        </TopRowBox>
        {tradePlan.savedConfigurationTitle ? (
          <TopRowBox
            backgroundColor={colors.grey}
            color={colors.lightGrey}
            width="205px"
            justifyContent={
              tradePlan.savedConfigurationTitle.length > 20
                ? 'flexStart'
                : 'center'
            }
          >
            {tradePlan.savedConfigurationTitle}
          </TopRowBox>
        ) : (
          <TopRowBox
            backgroundColor={colors.grey}
            color={colors.lightGrey}
            width="205px"
          >
            {definingSetupDetails(tradePlan)}
          </TopRowBox>
        )}
      </div>
      {/* <div className={style.secondRow}>
        <TradePlanIdentityDisplay tradePlanId={tradePlanId} />
      </div> */}
      <div className={style.thirdRow}>

        <TopRowFillPrice tradePlanId={tradePlanId} />
        <PnLDisplay
          tradePlanId={tradePlanId}
          symbol={tradePlan.positionSymbol ?? activeSymbol}
          dataSource={tradePlan.dataSource}
          brokerage={marketDataBrokerage}
        />
        <BidAskBox
          symbol={activeSymbol}
          dataSource={tradePlan.dataSource}
          brokerage={marketDataBrokerage}
        />
      </div>
      <div className={style.thirdRow}>
        {/* <CloseQtySection tradePlanId={tradePlanId} type={type} setType={setType}/> */}
        <OpenRow tradePlanId={tradePlanId} type={type} setType={setType}/>
      </div>
      <div className={style.thirdRow}>
        {/* <ClosePositionSection tradePlanId={tradePlanId} type={type}/>
         */}
        <CloseRow tradePlanId={tradePlanId} type={type}/>
      </div>
      <div className={style.bottomRow}>
        <TopRowExpandButton
          height= "24px"
          width= "520px"
          backgroundColor={colors.grey}
          fill={colors.lightGrey}
          clickHandler={() => tradePlanStore.flipExpandedState(tradePlanId)}
          expanded={tradePlan.expanded}
        />
      </div>
    </div>
  );
});

InPositionTopRow.propTypes = {
  expanded: PropTypes.bool,
  setExpanded: PropTypes.func,
  tradePlanId: PropTypes.string,
};
